import { useState, useEffect } from "react";
import React from "react";
import { getFirestore, collection, getDocs, getDoc, setDoc, doc, updateDoc } from "firebase/firestore";
import app from "./firebase";
import { Cookies, useCookies } from "react-cookie";
import Chart from "react-apexcharts";
import { Helmet } from "react-helmet";
import {
  Leaf, LayoutDashboard, History, Wallet, Settings, PhoneCall, BookOpen
  , LogOut, Menu, X, TrendingUp, Sprout, BarChart2,
  PlusCircle, ShoppingCart, ChevronRight, CheckCircle2, AlertCircle, Package, Bell, XCircle
} from "lucide-react";

const db = getFirestore(app);

const Dashboard = () => {
  const [toggle_new_request_bar, toggleBar] = useState(false);
  const [name, setName] = useState("Investor");
  const [item_list, setItemList] = useState([]);
  const [item_price, setItemPrice] = useState(0);
  const [cookies, removeCookie] = useCookies(["name"]);
  const [user_data, setUserData] = useState([]);
  const [wallet, setWallet] = useState("0.00");
  const [investment, setInvestment] = useState("0.00");
  const [escrow_total, setEscrowTotal] = useState("0.00");
  const [product_data, setProductData] = useState("");
  const [measurment, setMeasurment] = useState("");
  const [total_price, setTotalPrice] = useState("");
  const [quantity, setQuantity] = useState(0);
  const [item_category, setItemCategory] = useState("");
  const [item, setItem] = useState("");
  const [farm_invested_data, setFarmInvestedData] = useState([]);
  const [no_active_investment, setNoActiveInvestment] = useState(0);
  const [toast, setToast] = useState(null); // { type: "success"|"error", title, msg }
  const [show_mobile_menu, setMobileMenu] = useState(false);
  const [series, setSeries] = useState([1, 1]);
  const [pm, setPm] = useState(0);
  const [ratio, setRatio] = useCookies(["ratio"]);
  const [unreadBidsCount, setUnreadBidsCount] = useState(0);
  const [incomingBids, setIncomingBids] = useState([]);
  const [showTrendModal, setShowTrendModal] = useState(false);
  const [trendProduct, setTrendProduct] = useState(null);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [dynamicProducts, setDynamicProducts] = useState([]);
  const [emailVerified, setEmailVerified] = useState(true);

  const [options] = useState({
    chart: { type: "donut", toolbar: { show: false } },
    labels: ["Wallet", "Investment", "Escrowed"],
    colors: ["#1a8f46", "#22c55e", "#f59e0b"],
    dataLabels: { enabled: false },
    legend: { position: "bottom", fontFamily: "Inter, sans-serif", fontSize: "12px" },
    plotOptions: { pie: { donut: { size: "65%", labels: { show: true, total: { show: true, label: "Total Asset", fontFamily: "Inter, sans-serif", fontSize: "12px", color: "#0b4b2e" } } } } },
  });

  const userId = cookies.username;
  if (userId == undefined) { window.location.href = "/login"; }

  const showToast = (type, title, msg) => {
    setToast({ type, title, msg });
    setTimeout(() => setToast(null), 5000);
  };

  const updateItemCombo = async (event) => {
    const categoryId = event.target.value;
    setItemCategory(categoryId);
    if (categoryId && categoryId !== "0") {
      // Filter products by selected category
      const productsInCategory = dynamicProducts.filter(p => (p.category || "").toLowerCase() === categoryId.toLowerCase());
      setItemList(productsInCategory.map(p => p.name));
    } else {
      setItemList([]);
    }
  };

  const populateProductData = async (event) => {
    try {
      const productName = event.target.value;
      setItem(productName);
      const product = dynamicProducts.find(p => p.name === productName);
      if (product) {
        setProductData(product);
        setItemPrice(product.amount);
        setMeasurment(product.measurment);
      }
    } catch (error) { console.error(error); }
  };

  const updateQuantity = async (event) => {
    let current_quantity = event.target.value;
    if (current_quantity === "" || current_quantity === " ") current_quantity = 0;
    current_quantity = parseInt(current_quantity);
    let current_price = item_price;
    if (typeof current_price === "string") current_price = current_price.replace(",", "");
    current_price = parseInt(current_price);
    let total_price_a = current_price * current_quantity;
    total_price_a = (total_price_a).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,");
    setTotalPrice(total_price_a + "");
    setQuantity(current_quantity);
  };

  useEffect(() => {
    if (show_mobile_menu) document.body.classList.add("lock-scroll");
    else document.body.classList.remove("lock-scroll");
    return () => document.body.classList.remove("lock-scroll");
  }, [show_mobile_menu]);

  useEffect(() => {
    const initiateData = async () => {
      try {
        const docRef = doc(db, "users", userId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const ud = { id: docSnap.id, ...docSnap.data() };
          setUserData(ud);
          setName(ud.name);
          const current_wallet = ud.wallet || 0;
          const current_investment = ud.investment || 0;

          setWallet((current_wallet).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,"));
          setInvestment((current_investment).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,"));

          // Fetch Escrow Total
          const marketSnapshot = await getDocs(collection(db, "buyers_request"));
          const userRequests = marketSnapshot.docs
            .map(d => ({ ...d.data(), id: d.id }))
            .filter(item => item.id && item.id.startsWith(userId));

          const total_escrow = userRequests.reduce((sum, item) => sum + (parseFloat(item.escrowAmount) || 0), 0);
          setEscrowTotal(total_escrow.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,"));

          // Calculate Chart Data
          const w_val = current_wallet < 0.01 ? 0 : current_wallet;
          const i_val = current_investment < 0.01 ? 0 : current_investment;
          const e_val = total_escrow < 0.01 ? 0 : total_escrow;

          const total_assets = w_val + i_val + e_val;
          if (total_assets > 0) {
            setSeries([w_val, i_val, e_val]);
          } else {
            setSeries([1, 0, 0]); // Fallback
          }

          const r = i_val > 0 ? (i_val / (w_val + e_val)) * 10 : 0; // Simple PM ratio
          setPm(r.toFixed(1));
          setEmailVerified(ud.emailVerified !== false);
        }

        // Fetch incoming bids (for buyers)
        const proposalSnapshot = await getDocs(collection(db, "market_proposals"));
        const allProposals = proposalSnapshot.docs.map(d => ({ ...d.data(), id: d.id }));
        const userIncomingBids = allProposals.filter(p => p.buyerId === userId && !p.isRead);
        setIncomingBids(userIncomingBids);
        setUnreadBidsCount(userIncomingBids.length);

        const querySnapshot = await getDocs(collection(db, "investment"));
        const data = querySnapshot.docs.map(d => ({ ...d.data(), id: d.id }));
        setFarmInvestedData(data);
        let temp_count = 0;
        let temp_inv = 0;
        data.forEach(el => {
          if (el.owner === userId && el.investment_status === " active") {
            temp_count++;
            temp_inv += parseInt(el.price.replaceAll(",", ""));
          }
        });
        await updateDoc(doc(db, "users", userId), { investment: temp_inv });
        setNoActiveInvestment(temp_count);

        // Fetch products
        const productsSnapshot = await getDocs(collection(db, "products"));
        const products = productsSnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        setDynamicProducts(products);

        // Derive unique categories from products
        const uniqueCategories = [...new Set(products.map(p => p.category).filter(Boolean))];
        setDynamicCategories(uniqueCategories.map(c => ({ id: c, name: c })));
      } catch (error) { console.error("Error:", error); }
    };
    initiateData();
  }, []);

  const holdingDays = (date2) => {
    const parts = date2.split("/");
    const formattedDate2 = `${parts[2]}-${parts[1]}-${parts[0]}`;
    const current_date = new Date().setHours(0, 0, 0, 0);
    const date_2 = new Date(formattedDate2).setHours(0, 0, 0, 0);
    return Math.floor((current_date - date_2) / (1000 * 60 * 60 * 24));
  };

  const purchase = async () => {
    const c_wallet = parseInt(wallet.replaceAll(",", ""));
    const c_total_price = parseInt((total_price || "0").replaceAll(",", ""));
    if (!emailVerified) { showToast("error", "Email Unverified", "Please verify your email address to perform this action."); return; }
    if (total_price === "") { showToast("error", "No Item Selected", "Select the items you want to purchase."); return; }
    if (c_wallet < c_total_price) { showToast("error", "Insufficient Balance", "Your wallet balance is too low for this purchase."); return; }

    const categoryName = dynamicCategories.find(c => c.id === item_category)?.name || "Unknown";
    const unique_id = userId + "_" + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    const new_wallet = c_wallet - c_total_price;
    const new_investment = parseInt(investment.replaceAll(",", "")) + c_total_price;
    let date = new Date();
    const d = `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
    await setDoc(doc(db, "investment", unique_id), { name: item, category: categoryName, price: item_price, quantity, measurment, investment_status: " active", id: unique_id, owner: userId, date: d });
    await updateDoc(doc(db, "users", userId), { wallet: new_wallet, investment: new_investment });
    setWallet((new_wallet).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,"));
    setInvestment((new_investment).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,"));
    setQuantity("");
    setTotalPrice("");
    toggleBar(false);
    showToast("success", "Purchase Successful!", "Your investment has been activated.");
  };

  const sellItem = async (event) => {
    const id_temp_store = event.target.id.split("-");
    await updateDoc(doc(db, "investment", id_temp_store[0]), { investment_status: "sold" });
    let temp_investment = parseInt(id_temp_store[1].replaceAll(",", "")) * parseInt(id_temp_store[2]);
    const new_wallet = parseInt(wallet.replaceAll(",", "")) + temp_investment;
    await updateDoc(doc(db, "users", userId), { wallet: new_wallet });
    setWallet((new_wallet).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,"));
    setFarmInvestedData(prev => prev.map(i => i.id === id_temp_store[0] ? { ...i, investment_status: "sold" } : i));
    showToast("success", "Item Sold!", "Your item has been sold and wallet credited.");
  };

  // Trend analysis function
  const generateTrendData = (basePrice) => {
    // Generate trend data for past 90 days
    const now = new Date();
    const data7Days = [];
    const data30Days = [];
    const data90Days = [];

    for (let i = 89; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      // Simulate price fluctuation (±5% variation)
      const variation = (Math.random() - 0.5) * 0.1 * basePrice;
      const price = Math.max(basePrice * 0.85, basePrice + variation);

      if (i < 7) data7Days.push({ date: label, price: Math.round(price) });
      if (i < 30) data30Days.push({ date: label, price: Math.round(price) });
      data90Days.push({ date: label, price: Math.round(price) });
    }

    return { data7Days, data30Days, data90Days };
  };

  const showTrend = async (productName) => {
    try {
      const docRef = doc(db, "products", productName.toLowerCase().replace(/\\s+/g, "_"));
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const productInfo = { name: productName, ...docSnap.data() };
        setTrendProduct({ ...productInfo, trendData: generateTrendData(parseInt(String(productInfo.amount).replace(/,/g, ''))) });
        setShowTrendModal(true);
      }
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to load trend data");
    }
  };

  const activeInvestments = farm_invested_data.filter(i => i.owner === userId && i.investment_status === " active");

  const navItems = [
    { icon: <LayoutDashboard size={18} />, label: "Dashboard", action: null, active: true },
    { icon: <History size={18} />, label: "Investment History", action: () => { window.location.href = "/history"; } },
    { icon: <Wallet size={18} />, label: "Wallet", action: () => { window.location.href = "/wallet"; } },
    { icon: <Settings size={18} />, label: "Settings", action: () => { window.location.href = "/settings"; } },
    { icon: <ShoppingCart size={18} />, label: "Marketplace", action: () => { window.location.href = "/market_dashboard"; } },
    { icon: <BookOpen size={18} />, label: "Health & News (Blog)", action: () => { window.location.href = "/blog"; } },
    { icon: <PhoneCall size={18} />, label: "Contact Support", action: () => { window.location.href = "/contact"; } },
  ];

  return (
    <div style={{ fontFamily: "var(--font-sans)", display: "flex", minHeight: "100vh", background: "var(--color-gray-50)", paddingBottom: "40px" }}>
      <Helmet>
        <title>Dashboard — Agrivestia</title>
      </Helmet>

      {!emailVerified && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
          background: "#fff7ed", borderBottom: "1px solid #ffedd5",
          padding: "10px 20px", display: "flex", alignItems: "center",
          justifyContent: "center", gap: "12px", animation: "slideDown 0.4s ease"
        }}>
          <AlertCircle size={18} color="#f97316" />
          <span style={{ fontSize: "13px", color: "#9a3412", fontWeight: 600 }}>
            Your email is not verified. Please verify your email to enable all features.
          </span>
          <button
            onClick={() => window.location.href = `/verify_email?email=${encodeURIComponent(user_data.email)}`}
            style={{
              background: "#f97316", color: "white", border: "none",
              padding: "4px 12px", borderRadius: "6px", fontSize: "12px",
              fontWeight: 700, cursor: "pointer", boxShadow: "0 2px 4px rgba(249,115,22,0.2)"
            }}
          >
            Verify Now
          </button>
        </div>
      )}

      {/* ════ TOAST ════ */}
      {toast && (
        <div className="toast" style={{ borderLeft: `4px solid ${toast.type === "success" ? "var(--color-success)" : "var(--color-danger)"}` }}>
          <div className={`toast-icon ${toast.type === "success" ? "toast-success" : "toast-error"}`}>
            {toast.type === "success" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          </div>
          <div className="toast-body">
            <div className="toast-title">{toast.title}</div>
            <div className="toast-msg">{toast.msg}</div>
          </div>
          <button className="toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      {/* ════ DESKTOP SIDEBAR ════ */}
      <aside className="sidebar hide-mobile" style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
        {/* Logo */}
        <div style={{ padding: "24px 20px 16px", borderBottom: "1px solid var(--color-gray-100)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
            <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Leaf size={17} color="white" />
            </div>
            <span style={{ fontWeight: 800, fontSize: "17px", color: "var(--color-primary-dark)", letterSpacing: "-0.02em" }}>AGRIVESTIA</span>
          </div>

          {/* User chip */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "var(--gradient-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "15px", color: "white", flexShrink: 0 }}>
              {name ? name[0].toUpperCase() : "U"}
            </div>
            <div style={{ overflow: "hidden" }}>
              <div style={{ fontWeight: 600, fontSize: "13px", color: "var(--color-gray-800)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>
              <div style={{ fontSize: "11px", color: "var(--color-gray-400)", fontWeight: 500 }}>Agrivestia Farmer</div>
            </div>
          </div>
        </div>

        {/* Wallet pill */}
        <div style={{ margin: "16px", padding: "14px 16px", background: "var(--color-primary-light)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-accent-light)" }}>
          <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-primary)", letterSpacing: "0.05em", marginBottom: "4px" }}>WALLET BALANCE</div>
          <div style={{ fontSize: "20px", fontWeight: 800, color: "var(--color-primary-dark)", letterSpacing: "-0.02em" }}>₦{wallet}</div>
        </div>

        {/* Nav */}
        <nav style={{ padding: "0 12px", flex: 1, overflowY: "auto" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", letterSpacing: "0.07em", textTransform: "uppercase", padding: "4px 8px 10px" }}>Menu</div>
          {navItems.map((item, i) => (
            <button key={i} onClick={item.action}
              className={`sidebar-link ${item.active ? "active" : ""}`}
              style={{ width: "100%", border: "none", cursor: "pointer", marginBottom: "2px" }}>
              <span style={{ flexShrink: 0 }}>{item.icon}</span>
              <span style={{ fontSize: "14px" }}>{item.label}</span>
              {item.active && <ChevronRight size={14} style={{ marginLeft: "auto" }} />}
            </button>
          ))}
        </nav>

        {/* Logout */}
        <div style={{ padding: "12px 12px 20px", borderTop: "1px solid var(--color-gray-100)" }}>
          <button className="sidebar-link danger" onClick={() => { removeCookie("username"); window.location.href = "/login"; }}
            style={{ width: "100%", border: "none", cursor: "pointer" }}>
            <LogOut size={17} />
            <span style={{ fontSize: "14px" }}>Log Out</span>
          </button>
        </div>
      </aside>

      {/* ════ MOBILE DRAWER ════ */}
      {show_mobile_menu && (
        <>
          <div className="overlay" onClick={() => setMobileMenu(false)} />
          <div style={{
            position: "fixed", top: 0, left: 0, width: "80%", maxWidth: "320px",
            height: "100vh", background: "var(--color-white)", zIndex: "var(--z-modal)",
            padding: "24px", boxShadow: "var(--shadow-xl)",
            animation: "slideInLeft 0.3s cubic-bezier(0.16,1,0.3,1)",
            display: "flex", flexDirection: "column",
          }}>
            {/* Mobile Drawer Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Leaf size={16} color="white" />
                </div>
                <span style={{ fontWeight: 800, fontSize: "18px", color: "var(--color-primary-dark)" }}>AGRIVESTIA</span>
              </div>
              <button onClick={() => setMobileMenu(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-500)", padding: 0 }}>
                <X size={22} />
              </button>
            </div>

            {/* User chip */}
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px", paddingBottom: "20px", borderBottom: "1px solid var(--color-gray-100)" }}>
              <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "var(--gradient-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "15px", color: "white", flexShrink: 0 }}>
                {name ? name[0].toUpperCase() : "U"}
              </div>
              <div style={{ overflow: "hidden" }}>
                <div style={{ fontWeight: 600, fontSize: "13px", color: "var(--color-gray-800)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>
                <div style={{ fontSize: "11px", color: "var(--color-gray-400)", fontWeight: 500 }}>Agrivestia Farmer</div>
              </div>
            </div>

            {/* Wallet pill */}
            <div style={{ padding: "12px", background: "var(--color-primary-light)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-accent-light)", marginBottom: "24px" }}>
              <div style={{ fontSize: "10px", fontWeight: 600, color: "var(--color-primary)", letterSpacing: "0.05em", marginBottom: "4px" }}>WALLET BALANCE</div>
              <div style={{ fontSize: "16px", fontWeight: 800, color: "var(--color-primary-dark)", letterSpacing: "-0.02em" }}>₦{wallet}</div>
            </div>

            {/* Mobile Menu Items */}
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1, overflowY: "auto" }}>
              {navItems.map((item, i) => (
                <button key={i} onClick={() => { setMobileMenu(false); item.action && item.action(); }}
                  style={{ background: "none", border: "none", textAlign: "left", padding: "12px 12px", fontSize: "15px", fontWeight: 500, color: "var(--color-gray-700)", cursor: "pointer", borderRadius: "var(--radius-md)", transition: "background 0.15s", display: "flex", alignItems: "center", gap: "10px" }}
                  onMouseEnter={e => e.target.style.background = "var(--color-gray-50)"}
                  onMouseLeave={e => e.target.style.background = "none"}>
                  <span style={{ flexShrink: 0 }}>{item.icon}</span>
                  <span>{item.label}</span>
                  {item.active && <ChevronRight size={14} style={{ marginLeft: "auto" }} />}
                </button>
              ))}
            </div>

            {/* Logout button */}
            <button onClick={() => { setMobileMenu(false); removeCookie("username"); window.location.href = "/login"; }}
              style={{ background: "none", border: "none", textAlign: "left", padding: "12px 12px", fontSize: "15px", fontWeight: 500, color: "var(--color-danger)", cursor: "pointer", borderRadius: "var(--radius-md)", transition: "background 0.15s", display: "flex", alignItems: "center", gap: "10px", marginTop: "12px", paddingTop: "12px", borderTop: "1px solid var(--color-gray-100)" }}
              onMouseEnter={e => e.target.style.background = "var(--color-danger-light)"}
              onMouseLeave={e => e.target.style.background = "none"}>
              <LogOut size={17} />
              <span>Log Out</span>
            </button>
          </div>
        </>
      )}

      {/* ════ MAIN ════ */}
      <main className="main-content">
        {/* Top Bar */}
        <div style={{ background: "var(--color-white)", borderBottom: "1px solid var(--color-gray-100)", padding: "0 16px", minHeight: "64px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
          {/* Mobile Menu & Logo (Left) */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {/* Mobile hamburger */}
            <button onClick={() => setMobileMenu(!show_mobile_menu)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-600)", display: "none", padding: "4px" }}
              className="show-mobile">
              {show_mobile_menu ? <X size={24} /> : <Menu size={24} />}
            </button>

            <div className="show-mobile" style={{ display: "none", alignItems: "center", gap: "8px" }}
              onClick={() => window.location.href = "/"}>
              <div style={{ width: "30px", height: "30px", borderRadius: "8px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Leaf size={15} color="white" />
              </div>
              <span style={{ fontWeight: 800, fontSize: "14px", color: "var(--color-primary-dark)", letterSpacing: "-0.02em" }}>AGRIVESTIA</span>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div className="hide-mobile">
              <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--color-gray-900)" }}>Active Investments</div>
              <div style={{ fontSize: "12px", color: "var(--color-gray-400)", fontWeight: 400 }}>{new Date().toLocaleDateString("en-GB", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {/* Notification Bell */}
            <div style={{ position: "relative" }} title={unreadBidsCount > 0 ? `${unreadBidsCount} new bid(s)` : "No new bids"}>
              <button onClick={() => window.location.href = "/market_dashboard"}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-600)", padding: "8px", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                <Bell size={20} />
                {unreadBidsCount > 0 && (
                  <span style={{ position: "absolute", top: "2px", right: "0px", background: "var(--color-danger)", color: "white", fontSize: "10px", fontWeight: 900, borderRadius: "50%", minWidth: "18px", height: "18px", display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid white" }}>
                    {unreadBidsCount > 9 ? "9+" : unreadBidsCount}
                  </span>
                )}
              </button>
            </div>

            <button className="btn-primary hide-mobile" onClick={() => toggleBar(!toggle_new_request_bar)}
              style={{ gap: "6px", padding: "9px 18px", fontSize: "13px" }}>
              <PlusCircle size={15} /> New Investment
            </button>
            <button className="btn-primary show-mobile" onClick={() => toggleBar(!toggle_new_request_bar)}
              style={{ padding: "8px", minWidth: "36px", height: "36px", display: "none", justifyContent: "center" }}>
              <PlusCircle size={18} />
            </button>
          </div>
        </div>

        <div style={{ padding: "28px" }}>
          {/* ─── Stats Row ─── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px", marginBottom: "28px" }}>
            {/* Wallet */}
            <div className="card-stat" style={{ background: "var(--gradient-primary)", color: "white" }}>
              <div style={{ position: "absolute", top: "-20px", right: "-20px", width: "100px", height: "100px", borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "relative" }}>
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "rgba(255,255,255,0.7)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "8px" }}>Wallet Balance</div>
                  <div style={{ fontSize: "24px", fontWeight: 800, letterSpacing: "-0.02em" }}>₦{wallet}</div>
                </div>
                <div style={{ width: "42px", height: "42px", borderRadius: "12px", background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Wallet size={20} color="white" />
                </div>
              </div>
              <button onClick={() => window.location.href = "/wallet"} style={{ marginTop: "14px", background: "rgba(255,255,255,0.15)", border: "none", color: "rgba(255,255,255,0.9)", padding: "6px 12px", borderRadius: "var(--radius-sm)", fontSize: "12px", fontWeight: 600, cursor: "pointer", transition: "background 0.15s", display: "flex", alignItems: "center", gap: "4px" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.25)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}>
                Manage Wallet <ChevronRight size={13} />
              </button>
            </div>

            {/* Farm */}
            <div className="card-stat">
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: "var(--gradient-accent)", borderRadius: "var(--radius-lg) var(--radius-lg) 0 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-gray-500)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "8px" }}>Farm Balance</div>
                  <div style={{ fontSize: "24px", fontWeight: 800, color: "var(--color-gray-900)", letterSpacing: "-0.02em" }}>₦{investment}</div>
                </div>
                <div style={{ width: "42px", height: "42px", borderRadius: "12px", background: "var(--color-primary-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Sprout size={20} color="var(--color-primary)" />
                </div>
              </div>
              <div style={{ marginTop: "14px", fontSize: "12px", color: "var(--color-gray-500)" }}>
                <span className="badge badge-primary">{no_active_investment} active</span> investments
              </div>
            </div>

            {/* PM */}
            <div className="card-stat">
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: "linear-gradient(135deg, #f59e0b, #fbbf24)", borderRadius: "var(--radius-lg) var(--radius-lg) 0 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-gray-500)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "8px" }}>Escrowed Balance</div>
                  <div style={{ fontSize: "24px", fontWeight: 800, color: "var(--color-gray-900)", letterSpacing: "-0.02em" }}>₦{escrow_total}</div>
                </div>
                <div style={{ width: "42px", height: "42px", borderRadius: "12px", background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ShoppingCart size={20} color="#f59e0b" />
                </div>
              </div>
              <div style={{ marginTop: "14px", fontSize: "12px", color: "var(--color-gray-500)" }}>
                Locked in marketplace requests
              </div>
            </div>

            {/* Performance Ratio */}
            <div className="card-stat">
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: "linear-gradient(135deg, #3b82f6, #60a5fa)", borderRadius: "var(--radius-lg) var(--radius-lg) 0 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-gray-500)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "8px" }}>PM Index</div>
                  <div style={{ fontSize: "24px", fontWeight: 800, color: "var(--color-gray-900)", letterSpacing: "-0.02em" }}>{pm}</div>
                </div>
                <div style={{ width: "42px", height: "42px", borderRadius: "12px", background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <BarChart2 size={20} color="#3b82f6" />
                </div>
              </div>
              <div style={{ marginTop: "14px", fontSize: "12px", color: "var(--color-gray-500)" }}>Investment health score</div>
            </div>
          </div>

          {/* ─── Chart + New Investment Panel ─── */}
          <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: "20px", marginBottom: "28px" }}>
            {/* Chart */}
            <div className="card" style={{ padding: "24px" }}>
              <div style={{ fontSize: "14px", fontWeight: 700, color: "var(--color-gray-800)", marginBottom: "4px", display: "flex", alignItems: "center", gap: "8px" }}>
                <BarChart2 size={16} color="var(--color-primary)" /> Portfolio Split
              </div>
              <div style={{ fontSize: "12px", color: "var(--color-gray-400)", marginBottom: "16px" }}>Wallet vs Farm allocation</div>
              <Chart options={options} series={series} type="donut" width="100%" />
            </div>

            {/* New Investment Panel */}
            {toggle_new_request_bar ? (
              <div className="card" style={{ padding: "24px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                  <div>
                    <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-gray-800)" }}>New Investment</div>
                    <div style={{ fontSize: "12px", color: "var(--color-gray-400)", marginTop: "2px" }}>Select a crop category and item to invest</div>
                  </div>
                  <button onClick={() => toggleBar(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-400)" }}><X size={18} /></button>
                </div>
                <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                  <div className="input-group" style={{ marginBottom: 0 }}>
                    <label className="input-label">Category</label>
                    <select className="input-select" onChange={updateItemCombo}>
                      <option value="0">Select Category</option>
                      {dynamicCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="input-group" style={{ marginBottom: 0 }}>
                    <label className="input-label">Item</label>
                    <select className="input-select" onChange={populateProductData} id="item_drop_box">
                      <option value="">Select Item</option>
                      {item_list.map((it) => <option key={it} value={it}>{it}</option>)}
                    </select>
                  </div>
                </div>
                <div className="input-group" style={{ marginBottom: "16px" }}>
                  <label className="input-label">Quantity</label>
                  <input className="input-field" type="number" min="1" onChange={updateQuantity} value={quantity} placeholder="Enter quantity" />
                </div>

                {/* Price info */}
                <div style={{ display: "flex", gap: "12px", padding: "12px 16px", background: "var(--color-gray-50)", borderRadius: "var(--radius-md)", marginBottom: "20px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "13px", color: "var(--color-gray-600)" }}>Unit Price: <strong style={{ color: "var(--color-gray-800)" }}>₦{item_price}</strong></span>
                    <span style={{ color: "var(--color-gray-300)" }}>|</span>
                    <span style={{ fontSize: "13px", color: "var(--color-gray-600)" }}>Measurement: <strong style={{ color: "var(--color-gray-800)" }}>{measurment || "—"}</strong></span>
                    <span style={{ color: "var(--color-gray-300)" }}>|</span>
                    <span style={{ fontSize: "13px", color: "var(--color-gray-600)" }}>Total: <strong style={{ color: "var(--color-primary)", fontSize: "14px" }}>₦{total_price || "0"}</strong></span>
                  </div>
                  {item && (
                    <button onClick={() => showTrend(item)} style={{ fontSize: "12px", padding: "6px 12px", background: "white", border: "1px solid var(--color-primary)", borderRadius: "var(--radius-sm)", color: "var(--color-primary)", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}>
                      <TrendingUp size={14} /> View Trend
                    </button>
                  )}
                </div>

                <div style={{ display: "flex", gap: "12px" }}>
                  <button className="btn-primary" onClick={purchase} style={{ flex: 1, justifyContent: "center", padding: "12px" }}>
                    <ShoppingCart size={16} /> Purchase
                  </button>
                  <button className="btn-ghost" onClick={() => toggleBar(false)} style={{ flex: 1, justifyContent: "center", padding: "12px" }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="card" style={{ padding: "24px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: "12px" }}>
                <div style={{ width: "56px", height: "56px", borderRadius: "50%", background: "var(--color-primary-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <PlusCircle size={24} color="var(--color-primary)" />
                </div>
                <div style={{ fontWeight: 700, fontSize: "15px", color: "var(--color-gray-800)" }}>Start a New Investment</div>
                <div style={{ fontSize: "13px", color: "var(--color-gray-400)", maxWidth: "240px", lineHeight: 1.6 }}>Choose from 12+ premium agricultural products and start growing your wealth.</div>
                <button className="btn-primary" onClick={() => toggleBar(true)} style={{ marginTop: "4px", padding: "10px 22px", fontSize: "13px" }}>
                  <PlusCircle size={15} /> New Investment
                </button>
              </div>
            )}
          </div>

          {/* ─── Investments Table ─── */}
          <div className="card" style={{ padding: "0", overflow: "hidden" }}>
            {/* Table header */}
            <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--color-gray-100)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: "15px", color: "var(--color-gray-800)", display: "flex", alignItems: "center", gap: "8px" }}>
                  <Package size={16} color="var(--color-primary)" /> My Products
                </div>
                <div style={{ fontSize: "12px", color: "var(--color-gray-400)", marginTop: "2px" }}>{no_active_investment} active investment(s)</div>
              </div>
            </div>

            {/* Desktop Table */}
            <div className="hide-mobile" style={{ overflowX: "auto" }}>
              {activeInvestments.length === 0 ? (
                <div style={{ textAlign: "center", padding: "60px 24px", color: "var(--color-gray-400)" }}>
                  <Sprout size={36} color="var(--color-gray-300)" style={{ margin: "0 auto 12px" }} />
                  <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "6px" }}>No active investments</div>
                  <div style={{ fontSize: "13px" }}>Click "New Investment" to get started</div>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Value</th>
                      <th>Qty</th>
                      <th>Holding Days</th>
                      <th>Status</th>
                      <th style={{ textAlign: "right", paddingRight: "24px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeInvestments.map((inv, i) => (
                      <tr key={inv.id}>
                        <td style={{ fontWeight: 600, color: "var(--color-gray-400)", fontSize: "13px" }}>{i + 1}</td>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: "14px", color: "var(--color-gray-800)" }}>{inv.name}</div>
                          <div style={{ fontSize: "12px", color: "var(--color-gray-400)" }}>{inv.measurment}</div>
                        </td>
                        <td style={{ fontSize: "13px" }}>{inv.category}</td>
                        <td><span style={{ fontWeight: 600, color: "var(--color-primary-dark)" }}>₦{inv.price}</span></td>
                        <td><span className="badge badge-neutral">{inv.quantity}</span></td>
                        <td><span className="badge badge-primary">{holdingDays(inv.date)} day(s)</span></td>
                        <td><span className="badge badge-success">Active</span></td>
                        <td style={{ textAlign: "right", paddingRight: "24px" }}>
                          <button className="btn-danger" onClick={sellItem}
                            id={`${inv.id}-${inv.price}-${inv.quantity}`}
                            style={{ padding: "6px 16px", fontSize: "12px" }}>
                            Sell
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Mobile Cards */}
            <div className="show-mobile" style={{ padding: "16px", flexDirection: "column", gap: "12px" }}>
              {activeInvestments.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "var(--color-gray-400)", width: "100%" }}>
                  <Sprout size={32} color="var(--color-gray-300)" style={{ margin: "0 auto 10px" }} />
                  <div style={{ fontSize: "14px", fontWeight: 600 }}>No active investments</div>
                </div>
              ) : activeInvestments.map((inv, i) => (
                <div key={inv.id} className="card-stack-item">
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", alignItems: "center" }}>
                    <div style={{ fontWeight: 700, fontSize: "15px", color: "var(--color-gray-800)" }}>{inv.name}</div>
                    <span className="badge badge-success">Active</span>
                  </div>
                  <div className="card-stack-row"><span className="card-stack-label">Category</span><span className="card-stack-value">{inv.category}</span></div>
                  <div className="card-stack-row"><span className="card-stack-label">Value</span><span className="card-stack-value">₦{inv.price}</span></div>
                  <div className="card-stack-row"><span className="card-stack-label">Quantity</span><span className="card-stack-value">{inv.quantity}</span></div>
                  <div className="card-stack-row" style={{ borderBottom: "none" }}><span className="card-stack-label">Holding</span><span className="card-stack-value">{holdingDays(inv.date)} days</span></div>

                  <button className="btn-danger" onClick={sellItem}
                    id={`${inv.id}-${inv.price}-${inv.quantity}`}
                    style={{ width: "100%", justifyContent: "center", marginTop: "14px", padding: "10px" }}>
                    Sell Investment
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Trend Analysis Modal */}
      {showTrendModal && trendProduct && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}>
          <div style={{ background: "white", borderRadius: "var(--radius-lg)", padding: "28px", maxWidth: "900px", width: "95%", maxHeight: "90vh", overflow: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
              <div>
                <h2 style={{ fontSize: "20px", fontWeight: 700, color: "var(--color-gray-900)", marginBottom: "4px" }}>{trendProduct.name} - Price Trend Analysis</h2>
                <p style={{ fontSize: "13px", color: "var(--color-gray-500)" }}>Current Price: <strong>₦{String(trendProduct.amount).includes(",") ? trendProduct.amount : String(trendProduct.amount).replace(/\\B(?=(\\d{3})+(?!\\d))/g, ",")}</strong></p>
              </div>
              <button onClick={() => setShowTrendModal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-400)" }}>
                <XCircle size={24} />
              </button>
            </div>

            {/* Tab-style selector for different periods */}
            <div style={{ display: "flex", gap: "12px", marginBottom: "24px", borderBottom: "1px solid var(--color-gray-100)", paddingBottom: "12px", flexWrap: "wrap" }}>
              <button id="trend-7days" onClick={() => document.getElementById('trend-7days').style.opacity = '1'} style={{ padding: "8px 16px", border: "none", background: "none", cursor: "pointer", fontWeight: 600, fontSize: "14px", color: "var(--color-primary)", borderBottom: "2px solid var(--color-primary)" }}>
                Last 7 Days
              </button>
              <button id="trend-30days" onClick={() => document.getElementById('trend-30days').style.opacity = '1'} style={{ padding: "8px 16px", border: "none", background: "none", cursor: "pointer", fontWeight: 600, fontSize: "14px", color: "var(--color-gray-400)" }}>
                Last 1 Month
              </button>
              <button id="trend-90days" onClick={() => document.getElementById('trend-90days').style.opacity = '1'} style={{ padding: "8px 16px", border: "none", background: "none", cursor: "pointer", fontWeight: 600, fontSize: "14px", color: "var(--color-gray-400)" }}>
                Last 3 Months
              </button>
            </div>

            {/* Charts */}
            <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "24px" }}>
              <div>
                <h4 style={{ fontSize: "14px", fontWeight: 700, color: "var(--color-gray-800)", marginBottom: "12px" }}>Last 7 Days</h4>
                <Chart
                  type="area"
                  series={[{ name: "Price", data: trendProduct.trendData.data7Days.map(d => d.price) }]}
                  options={{
                    chart: { toolbar: { show: false } },
                    xaxis: { categories: trendProduct.trendData.data7Days.map(d => d.date) },
                    colors: ["#22c55e"],
                    responsive: [{ breakpoint: 480, options: { chart: { height: 250 } } }]
                  }}
                  height={250}
                />
              </div>

              <div>
                <h4 style={{ fontSize: "14px", fontWeight: 700, color: "var(--color-gray-800)", marginBottom: "12px" }}>Last 1 Month</h4>
                <Chart
                  type="area"
                  series={[{ name: "Price", data: trendProduct.trendData.data30Days.map(d => d.price) }]}
                  options={{
                    chart: { toolbar: { show: false } },
                    xaxis: { categories: trendProduct.trendData.data30Days.map(d => d.date), tickPlacement: "on" },
                    colors: ["#f59e0b"],
                    responsive: [{ breakpoint: 480, options: { chart: { height: 250 } } }]
                  }}
                  height={250}
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <h4 style={{ fontSize: "14px", fontWeight: 700, color: "var(--color-gray-800)", marginBottom: "12px" }}>Last 3 Months</h4>
                <Chart
                  type="area"
                  series={[{ name: "Price", data: trendProduct.trendData.data90Days.map(d => d.price) }]}
                  options={{
                    chart: { toolbar: { show: false } },
                    xaxis: { categories: trendProduct.trendData.data90Days.map(d => d.date), tickPlacement: "on" },
                    colors: ["#8b5cf6"],
                    responsive: [{ breakpoint: 480, options: { chart: { height: 250 } } }]
                  }}
                  height={250}
                />
              </div>
            </div>

            <div style={{ marginTop: "24px", paddingTop: "20px", borderTop: "1px solid var(--color-gray-100)", textAlign: "center" }}>
              <button onClick={() => setShowTrendModal(false)} style={{ padding: "10px 24px", background: "var(--color-primary)", color: "white", border: "none", borderRadius: "var(--radius-md)", fontWeight: 600, cursor: "pointer" }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
